import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';
import { Button } from '../components/common';

const RULES = [
  { key: 'length',  label: 'At least 8 characters',         test: (p)     => p.length >= 8 },
  { key: 'upper',   label: 'At least one uppercase letter',  test: (p)     => /[A-Z]/.test(p) },
  { key: 'lower',   label: 'At least one lowercase letter',  test: (p)     => /[a-z]/.test(p) },
  { key: 'number',  label: 'At least one number',            test: (p)     => /[0-9]/.test(p) },
  { key: 'match',   label: 'Passwords match',                test: (p, c)  => p.length > 0 && p === c },
];

const EyeIcon = ({ visible }) => visible ? (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

/**
 * MyProfile - User profile management page
 */
const MyProfile = ({ user, onUpdate, onCancel }) => {
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName]   = useState(user?.lastName  || '');
  const [email, setEmail]         = useState(user?.email     || '');
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [anyFocus, setAnyFocus]   = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [saving, setSaving]       = useState(false);

  const showRules = anyFocus && (password.length > 0 || confirm.length > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password && password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      const data = { firstName, lastName, email };
      if (password) data.password = password;

      await api.put(`/users/${user.id}`, data);
      setSuccess('Profile updated successfully!');
      setPassword('');
      setConfirm('');
      setAnyFocus(false);

      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const eyeBtn = (
    <button
      type="button"
      onClick={() => setShowPassword(v => !v)}
      style={{
        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        color: 'var(--color-text-muted)', fontSize: 16, lineHeight: 1,
      }}
      tabIndex={-1}
      aria-label={showPassword ? 'Hide password' : 'Show password'}
    >
      <EyeIcon visible={showPassword} />
    </button>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Profile</h1>
          <div className="page-subtitle">Update your profile and password</div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '440px' }}>
        {error   && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">First Name</label>
            <input
              className="form-input"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Last Name</label>
            <input
              className="form-input"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <div style={{ borderTop: '1px solid var(--color-border)', margin: '20px 0 16px' }} />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            Change Password{' '}
            <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(leave blank to keep current)</span>
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setAnyFocus(true)}
                autoComplete="new-password"
                style={{ paddingRight: 36 }}
              />
              {eyeBtn}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onFocus={() => setAnyFocus(true)}
                autoComplete="new-password"
                style={{ paddingRight: 36 }}
              />
              {eyeBtn}
            </div>
          </div>

          {showRules && (
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', fontSize: 13 }}>
              {RULES.map(rule => {
                const met = rule.test(password, confirm);
                return (
                  <li key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0', color: met ? '#16a34a' : '#dc2626' }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{met ? '✓' : '✗'}</span>
                    {rule.label}
                  </li>
                );
              })}
            </ul>
          )}

          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

MyProfile.propTypes = {
  user: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
  onCancel: PropTypes.func,
};

export default MyProfile;
